import {
  ChevronDown,
  ChevronRight,
  Compass,
  KeyRound,
  LogOut,
  Menu as MenuIcon,
  Monitor,
  Moon,
  Sun,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import { useAuth } from '../auth';
import { Avatar, Button } from '../components/kit';
import { Menu } from '../components/overlays';
import { titleCase } from '../lib/format';
import { findNav, NAV } from './nav';
import { LogoTile } from '../components/logo';
import { tourDone, TourProvider, useTour } from '../components/tour';
import { ADMIN_TOUR } from './tour-steps';

type Theme = 'system' | 'light' | 'dark';

function readTheme(): Theme {
  try {
    const t = localStorage.getItem('wms.theme');
    return t === 'light' || t === 'dark' ? t : 'system';
  } catch {
    return 'system';
  }
}

function applyTheme(t: Theme) {
  if (t === 'system') delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = t;
  try {
    localStorage.setItem('wms.theme', t);
  } catch {
    /* storage unavailable: theme still applies for this visit */
  }
}

export function AppShell({ children, crumb }: { children: ReactNode; crumb?: string }) {
  return (
    <TourProvider steps={ADMIN_TOUR}>
      <Shell crumb={crumb}>{children}</Shell>
    </TourProvider>
  );
}

function Shell({ children, crumb }: { children: ReactNode; crumb?: string }) {
  const { me, can, logout } = useAuth();
  const tour = useTour();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState<Theme>(readTheme);
  useEffect(() => applyTheme(theme), [theme]);
  useEffect(() => setOpen(false), [location.pathname]);
  // First visit to the dashboard: operator admins get the guided tour once.
  const autoTour =
    Boolean(me?.roles.includes('operator_admin')) &&
    !me?.passwordChangeRequired &&
    location.pathname === '/' &&
    !tourDone(me?.id ?? '');
  const { start } = tour;
  useEffect(() => {
    if (!autoTour) return;
    const t = window.setTimeout(start, 700);
    return () => window.clearTimeout(t);
  }, [autoTour, start]);

  const here = findNav(location.pathname);
  const sections = NAV.map((s) => ({
    ...s,
    items: s.items.filter((i) => !i.permission || can(i.permission)),
  })).filter((s) => s.items.length);
  const ThemeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor;
  const nextTheme: Record<Theme, Theme> = { system: 'light', light: 'dark', dark: 'system' };

  return (
    <div className="shell">
      <div className={`scrim ${open ? 'open' : ''}`} onClick={() => setOpen(false)} />
      <aside className={`sidebar ${open ? 'open' : ''}`} aria-label="Main navigation">
        <div className="brand">
          <span className="brand-mark">
            <LogoTile size={32} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="brand-name">Zenarty WMS</div>
            <div className="brand-sub">{me?.operatorName}</div>
          </div>
        </div>
        {me?.passwordChangeRequired ? null : (
          <nav>
            {sections.map((s) => (
              <div key={s.title}>
                <div className="nav-section">{s.title}</div>
                {s.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/' || item.to === '/inventory'}
                    className="nav-link"
                  >
                    <item.icon aria-hidden />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
        )}
        <div className="sidebar-foot">
          <span>Signed in as {titleCase(me?.roles[0] ?? '')}</span>
          <span>Every action is recorded in the audit log</span>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <Button
            variant="ghost"
            className="menu-button"
            icon={MenuIcon}
            aria-label="Open menu"
            onClick={() => setOpen(true)}
          />
          <div className="crumbs">
            <span>{here?.section ?? 'Account'}</span>
            <ChevronRight aria-hidden />
            <span className="current">{crumb ?? here?.item.label ?? 'Settings'}</span>
          </div>
          <span className="spacer" />
          <Button
            variant="ghost"
            icon={ThemeIcon}
            aria-label={`Theme: ${theme}. Switch to ${nextTheme[theme]}`}
            title={`Theme: ${theme}`}
            onClick={() => setTheme(nextTheme[theme])}
          />
          <Menu
            trigger={(toggle) => (
              <button type="button" onClick={toggle} aria-haspopup="menu">
                <Avatar name={me?.name ?? ''} />
                <span className="who">
                  <strong>{me?.name}</strong>
                  <small>{me?.email}</small>
                </span>
                <ChevronDown size={16} className="muted" />
              </button>
            )}
          >
            {(close) => (
              <>
                <div className="menu-head">
                  <strong>{me?.name}</strong>
                  <div className="muted small">{me?.roles.map(titleCase).join(', ')}</div>
                </div>
                <div className="menu-sep" />
                {me?.passwordChangeRequired ? null : (
                  <button
                    type="button"
                    className="menu-item"
                    onClick={() => {
                      close();
                      tour.start();
                    }}
                  >
                    <Compass /> Take the tour
                  </button>
                )}
                <NavLink to="/account/password" className="menu-item" onClick={close}>
                  <KeyRound /> Change password
                </NavLink>
                <button
                  type="button"
                  className="menu-item"
                  onClick={() => {
                    close();
                    void logout();
                  }}
                >
                  <LogOut /> Sign out
                </button>
              </>
            )}
          </Menu>
        </header>
        <main className="content">{children}</main>
      </div>
    </div>
  );
}
